import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Flex, Icon } from '../../../../components/common';
import { motCardClickEvent } from '../../../../services/analyticsService';
import Styled from 'styled-components';

/* MOT Permit Information — Figma LP-5096 (light-blue card with seal-check watermark). */
const Card = Styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #E1F2F0;
  background: #EAF1FB;
  width: 369px;
  max-width: 100%;
  min-height: 205px;
  margin-inline: auto;

  @media only screen and (max-width: 991px) {
    margin-block: 12px;
  }
`;

/* Decorative seal-check watermark, clipped at the bottom corner. */
const Watermark = Styled.div`
  position: absolute;
  bottom: -40px;
  inset-inline-end: -30px;
  z-index: 0;
  pointer-events: none;
  color: #D5E3F6;
  line-height: 0;

  svg { width: 121.37px; height: 133.5px; }
`;

const Inner = Styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = Styled.p`
  color: #222222;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  margin: 0;
`;

const WhiteBox = Styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
  padding: 12px;
  border-radius: 8px;
  background: #FFFFFF;

  .label { color: #626262; font-size: 12px; font-weight: 500; line-height: 18px; margin: 0; }
  .value { color: #222222; font-size: 14px; font-weight: 600; line-height: 20px; margin: 0;
           white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const InlineField = Styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;

  .label { color: #707070; font-size: 12px; font-weight: 500; line-height: 18px; margin: 0; flex-shrink: 0; }
  .value { color: #222222; font-size: 14px; font-weight: 600; line-height: 20px; margin: 0;
           white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const DateRow = Styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  .item { display: flex; align-items: baseline; gap: 10px; }
  .label { color: #707070; font-size: 12px; font-weight: 500; line-height: 18px; margin: 0; }
  .value { color: #222222; font-size: 14px; font-weight: 600; line-height: 20px; margin: 0; }
  .sep { width: 1px; height: 20px; background: #C9D8EC; }
`;

const MotCard = ({ listing }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.app.loginUser);
  // Always render the card + title (daily-rental only via parent gating); value
  // rows below are shown only when their value is present.
  const motData = listing?.mot_details || {};

  const handleClick = () => {
    motCardClickEvent(user);
  };

  return (
    <Card onClick={handleClick} role="group">
      <Watermark aria-hidden="true">
        <Icon icon="PiSealCheckFill" />
      </Watermark>

      <Inner>
        <Flex align="center" gap="8px">
          <Title>{t('MOT Permit Information')}</Title>
          <Icon icon="PiSealCheckFill" color="#3E7BFA" style={{ width: 12, height: 12 }} />
        </Flex>

        <Flex gap="16px" align="stretch">
          <WhiteBox>
            <p className="label">{t('Permit Number')}</p>
            <p className="value">{motData.permit_number || '-'}</p>
          </WhiteBox>
          <WhiteBox>
            <p className="label">{t('CR Number')}</p>
            <p className="value">{motData.legal_id || '-'}</p>
          </WhiteBox>
        </Flex>

        <InlineField>
          <p className="label">{t('Commercial Name')}</p>
          <p className="value">{motData.commercial_name || '-'}</p>
        </InlineField>

        <DateRow>
          <div className="item">
            <p className="label">{t('Issue Date')}</p>
            <p className="value">{motData.issue_date || '-'}</p>
          </div>
          <span className="sep" />
          <div className="item">
            <p className="label">{t('Expiry Date')}</p>
            <p className="value">{motData.expiry_date || '-'}</p>
          </div>
        </DateRow>
      </Inner>
    </Card>
  );
};

export default MotCard;
